import React__default, { forwardRef, useCallback } from 'react';
import styled from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { CSSDefaultInputField } from '../fields/common.js';
import { setErrorDataAttribute } from '../fields/FieldError.js';
import { __Icon } from '../icon/Icon.js';
import { Stack } from '../layout/Stack.js';
import { getThemeVariantColor } from '../styles/stylePropTypes.js';
import { setDisabledDataAttribute, InnerInputLabel } from './InputCommon.js';

function isInputTypeNumber(inputProps, type) {
    return type === "number" || type === "range";
}
function isInputTypeText(inputProps, type) {
    return !isInputTypeNumber(inputProps, type);
}
const StyledTextInput = styled.input`
  ${CSSDefaultInputField}
  caret-color: ${getThemeVariantColor("context")};
  &[type='number'] {
    /* Chrome, Safari, Edge, Opera */
    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    /* Firefox */
    -moz-appearance: textfield;
  }
`;
/**
 * Text Input accepts custom + all standard text input props except for `placeholder` - label should be provided instead
 *
 * @example
 * <TextInput label="Make a note..." value="initial value" />
 */ const TextInput = forwardRef(({ disabled =false , hasError =false , icon , label , testId ="X" , value , type ="text" , ...props }, ref)=>{
    const onChange = useCallback((e)=>{
        if (props.onChange && !disabled) {
            if (isInputTypeNumber(props, type)) {
                const parsed = parseFloat(e.target.value);
                const val = isNaN(parsed) ? "" : parsed;
                props.onChange(val);
            } else if (isInputTypeText(props, type)) {
                props?.onChange(e.target.value);
            }
        }
    }, [
        props.onChange
    ]);
    const onBlur = useCallback((e)=>{
        if (props.onBlur && !disabled) {
            if (isInputTypeNumber(props, type)) {
                const parsed = parseFloat(e.target.value);
                const val = isNaN(parsed) ? "" : parsed;
                props.onBlur(val);
            } else if (isInputTypeText(props, type)) {
                props?.onBlur(e.target.value);
            }
        }
    }, [
        props.onBlur
    ]);
    const onFocus = useCallback((e)=>{
        if (props.onFocus && !disabled) {
            if (isInputTypeNumber(props, type)) {
                const parsed = parseFloat(e.target.value);
                const val = isNaN(parsed) ? "" : parsed;
                props.onFocus(val);
            } else if (isInputTypeText(props, type)) {
                props?.onFocus(e.target.value);
            }
        }
    }, [
        props.onFocus
    ]);
    return React__default.createElement(Stack, {
        "data-hpy-web-text-input": true,
        ...!!icon && {
            "data-hpy-web-text-input-has-icon": !!icon
        },
        ...setDisabledDataAttribute(disabled),
        ...setErrorDataAttribute(hasError)
    }, React__default.createElement(StyledTextInput, {
        ...props,
        ...setTestId("TextInput-" + testId),
        ref: ref,
        "data-hpy-web-text-input-input": true,
        type: type,
        disabled: disabled,
        placeholder: label,
        value: value,
        onChange: onChange,
        onBlur: onBlur,
        onFocus: onFocus
    }), React__default.createElement(InnerInputLabel, {
        "data-hpy-web-text-input-input-inner-label": true,
        size: "small"
    }, label), !!icon && React__default.createElement(__Icon, {
        "data-hpy-web-text-input-input-icon": true,
        icon: icon
    }));
});
TextInput.dataId = "data-hpy-web-text-input";
TextInput.displayName = "TextInput";

export { TextInput };
